import React, { useImperativeHandle, useState } from 'react'
import { createPortal } from 'react-dom'

import { TooltipProvider } from '@/components/ui/tooltip'
import { getShapeChromeScreenRect } from '@/lib/editor/floating-canvas-chrome-bounds'
import {
  ToolbarGripButton,
  ToolbarResetButton,
  stopPropagation,
  useBlockBrowserZoomRef,
  useDraggableToolbarAnchor,
  useFloatingChromePosition,
} from '@/components/app/editor/tools/floating-toolbar'

import { FlipButtons } from './components/flip-buttons'
import { TH, TW } from './constants/crop-toolbar-dimensions'
import { computeCropToolbarPos } from './helpers/crop-toolbar-placement'
import type {
  CropToolbarHoveredButton,
  CropToolbarProps,
  CropToolbarRef,
} from './types/crop-toolbar-props'

export type { CropToolbarProps, CropToolbarRef } from './types/crop-toolbar-props'

export const CropToolbar = React.forwardRef<CropToolbarRef, CropToolbarProps>(
  ({ shape, scale, canvasRef, onFlipVertical, onFlipHorizontal, onReset, resetDisabled }, ref) => {
    const [hoveredButton, setHoveredButton] = useState<CropToolbarHoveredButton>(null)
    const { rootRef, setRootRef } = useBlockBrowserZoomRef()
    const pos = useFloatingChromePosition(canvasRef, shape, scale, computeCropToolbarPos)
    const {
      displayPos,
      isDragging,
      handleDragStart,
      handleDragMove,
      handleDragEnd,
      cancelActiveDrag,
    } = useDraggableToolbarAnchor({
      shape,
      scale,
      canvasRef,
      pos,
      width: TW,
      height: TH,
      getShapeRect: getShapeChromeScreenRect,
      revertOnCancel: true,
    })

    useImperativeHandle(ref, () => ({ cancelActiveDrag }), [cancelActiveDrag])

    if (!displayPos) return null

    // One above the zoom slider's `zIndex: 9999`: the tooltips portal into this root, so its
    // stacking context must outrank the slider or they render underneath it.
    return createPortal(
      <div
        ref={setRootRef}
        style={{ position: 'fixed', left: displayPos.x, top: displayPos.y, zIndex: 10000 }}
        onPointerDown={stopPropagation}
        onPointerUp={stopPropagation}
        onClick={stopPropagation}
      >
        <TooltipProvider delayDuration={0}>
          <div
            className="flex items-center gap-2 rounded-[14px] border border-black/[0.07] bg-white"
            style={{
              paddingTop: 8,
              paddingRight: 8,
              paddingBottom: 8,
              paddingLeft: 4,
              boxShadow: '0 4px 20px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.06)',
            }}
          >
            <ToolbarGripButton
              isDragging={isDragging}
              onDragStart={handleDragStart}
              onDragMove={handleDragMove}
              onDragEnd={handleDragEnd}
            />

            <div className="-ml-1 h-5 w-px flex-shrink-0 bg-black/[0.12]" />

            <FlipButtons
              hoveredButton={hoveredButton}
              setHoveredButton={setHoveredButton}
              onFlipVertical={onFlipVertical}
              onFlipHorizontal={onFlipHorizontal}
              tooltipContainer={rootRef.current}
            />

            {/* Reset */}
            <ToolbarResetButton
              onReset={onReset}
              disabled={resetDisabled}
              enabledTooltip="Reset to original crop"
              isHovered={hoveredButton === 'reset'}
              onHoverStart={() => setHoveredButton('reset')}
              onHoverEnd={() => setHoveredButton(null)}
              tooltipContainer={rootRef.current}
            />
          </div>
        </TooltipProvider>
      </div>,
      document.body
    )
  }
)

CropToolbar.displayName = 'CropToolbar'
